<template>
  <v-card elevation="0">
    <s-toolbar v-if="visibleTitle" label="Datos Persona" />
    <v-card-text>
      <!-- Sección 1: Identificación del trabajador -->
      <v-row>
        <v-col class="s-col-form" sm="4" md="3" lg="4">
          <s-toolbar-person
            ref="PersonRef"
            :defaultDocument="item.PrsDocumentNumber"
            @returnPerson="returnPerson($event, item)"
            v-model="item.PrsDocumentNumber"
            noSearch
            noCamera
            readonly
            :showCounter="true"
            :disabled="extension ? extension : renovation"
          />
        </v-col>
        <v-col class="s-col-form" cols="8" sm="8" md="8" lg="8">
          <s-text
            ref="textNtpName"
            label="Nombres y Apellidos Completos"
            v-model="item.NtpFullName"
            readonly
            :disabled="extension ? extension : renovation"
          />
        </v-col>
      </v-row>

      <!-- Sección 2: Origen / Requerimiento -->
      <v-row>
        <v-col class="s-col-form" sm="4" md="4" lg="4">
          <s-select-definition
            ref="payrollRef"
            :def="1060"
            label="Planilla"
            v-model="item.TypePayroll"
            :add="$fun.isAdmin()"
            :readonly="extension ? extension : renovation"
            :disabled="isPrimaryFieldDisabled"
          />
        </v-col>
        <v-col
          v-if="hasSourceRequirements"
          class="s-col-form"
          sm="4"
          md="4"
          lg="4"
        >
          <s-select
            label="Requerimiento de origen"
            :items="sourceRequirementItems"
            item-text="text"
            item-value="value"
            v-model="selectedSourceRequirementKey"
            clearable
            :disabled="extension ? extension : renovation"
          ></s-select>
        </v-col>
        <v-col
          v-if="sourceLabel"
          class="s-col-form"
          sm="4"
          md="4"
          lg="4"
        >
          <s-text label="Origen" :value="sourceLabel" readonly />
        </v-col>
      </v-row>

      <!-- Helper de Vacantes del Requerimiento -->
      <v-row v-if="item.ReqID" class="px-3">
        <v-col cols="12" class="pt-0 pb-2">
          <v-alert
            type="info"
            outlined
            dense
            prominent
            icon="mdi-briefcase-account"
            class="mb-0"
          >
            <div class="d-flex flex-wrap align-center justify-space-between">
              <span class="font-weight-medium mr-4">
                Estado de Cobertura del Requerimiento #{{ item.ReqID }}:
              </span>
              <div class="d-flex gap-4 align-center">
                <v-chip color="primary" outlined class="mr-2">
                  <v-icon left small>mdi-numeric-1-box</v-icon>
                  Vacantes: {{ vacancyStatus.TotalVacancies }}
                </v-chip>
                <v-chip color="success" outlined class="mr-2">
                  <v-icon left small>mdi-check-circle</v-icon>
                  Cubiertas: {{ vacancyStatus.CoveredVacancies }}
                </v-chip>
                <v-chip color="warning" outlined>
                  <v-icon left small>mdi-file-document</v-icon>
                  Contratos Activos: {{ vacancyStatus.ActiveContracts }}
                </v-chip>
              </div>
            </div>

            <!-- Desglose por Turno y Género -->
            <div v-if="hasProjections" class="mt-3 pt-3 border-t-dotted">
              <div class="text-subtitle-2 mb-2 font-weight-bold">Cruce de Proyección Semanal y Contratos Activos:</div>
              <v-simple-table dense class="transparent-table">
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-left font-weight-bold">Turno</th>
                      <th class="text-center font-weight-bold">Proyectado</th>
                      <th class="text-center font-weight-bold">Contratados</th>
                      <th class="text-center font-weight-bold">Meta Hombres</th>
                      <th class="text-center font-weight-bold">Hombres Activos</th>
                      <th class="text-center font-weight-bold">Meta Mujeres</th>
                      <th class="text-center font-weight-bold">Mujeres Activas</th>
                      <th class="text-right font-weight-bold">Vacantes Libres</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="t in parsedProjections" :key="t.TypeTurn">
                      <td>{{ t.TurnName || 'Sin Turno' }}</td>
                      <td class="text-center">{{ t.TotalProyectado }}</td>
                      <td class="text-center">{{ t.TotalActivos }}</td>
                      <td class="text-center">{{ t.MaleProyectado }}</td>
                      <td class="text-center">{{ t.MaleActivos }}</td>
                      <td class="text-center">{{ t.FemaleProyectado }}</td>
                      <td class="text-center">{{ t.FemaleActivos }}</td>
                      <td class="text-right font-weight-bold" :class="(t.TotalProyectado - t.TotalActivos) > 0 ? 'success--text' : 'grey--text'">
                        {{ t.TotalProyectado - t.TotalActivos }}
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </div>
          </v-alert>
        </v-col>
      </v-row>

      <!-- Sección 3: Ubicación organizacional -->
      <v-row>
        <v-col class="s-col-form" sm="4" md="4" lg="4">
          <s-select-area
            ref="areaRef"
            :key="`area-${item.AreID || 0}-${item.AreName || ''}`"
            label="Área"
            v-model="item.AreID"
            :text="item.AreName"
            full
            :disabled="isPrimaryFieldDisabled"
          ></s-select-area>
        </v-col>
        <v-col class="s-col-form" sm="4" md="4" lg="4">
          <s-select-area
            ref="centerCostRef"
            :key="`cce-${selectedCenterCost || 0}-${item.CceName || ''}`"
            v-model="selectedCenterCost"
            :text="item.CceName"
            centercost
            :clearable="true"
            label="Centro de Costo"
            full
            :disabled="isPrimaryFieldDisabled"
          />
        </v-col>
        <v-col class="s-col-form" sm="4" md="4" lg="4">
          <s-select-business-line
            ref="businessLineRef"
            :key="`bsl-${selectedCenterCost || 0}-${selectedBusinessLine || 0}`"
            v-model="selectedBusinessLine"
            :cceID="selectedCenterCost"
            :clearable="true"
            label="Línea de Negocio"
            :disabled="isPrimaryFieldDisabled"
          />
        </v-col>
      </v-row>

      <!-- Sección 4: Puesto y clasificación -->
      <v-row>
        <v-col class="s-col-form" sm="4" md="4" lg="4">
          <s-select-position
            ref="positionRef"
            :key="`pst-${item.AreID || 0}-${item.PstID || 0}-${item.PstName || ''}`"
            v-model="item.PstID"
            :text="item.PstName"
            :AreID="item.AreID || 0"
            centercost
            :clearable="true"
            label="Cargo"
            :disabled="isPrimaryFieldDisabled"
          />
        </v-col>
        <v-col class="s-col-form" sm="4" md="4" lg="4">
          <v-label>
            <b> Nivel de Criticidad </b>
          </v-label>
          <div class="mt-1">
            <v-chip
              :color="
                item.PstIsBascCritic == 1 || item.PstIsBascCritic == 0
                  ? 'white'
                  : item.PstIsBascCritic == 2
                  ? 'primary'
                  : item.PstIsBascCritic == 3
                  ? 'warning'
                  : 'error'
              "
              style="margin:0px;"
              >{{ item.PstIsBascCritic == 0 ? "No Definido" : item.PstIsBascCriticName }}
            </v-chip>
          </div>
        </v-col>
        <v-col class="s-col-form" sm="4" md="4" lg="4">
          <s-select
            label="Cuadrilla"
            :items="quadrille"
            autocomplete
            clearable
            item-text="GngName"
            item-value="GngID"
            v-model="item.GngID"
            :readonly="extension ? extension : renovation"
            :disabled="extension ? extension : renovation"
          ></s-select>
        </v-col>
      </v-row>

      <!-- Sección 5: Tipo de contrato y situación -->
      <v-row>
        <v-col class="s-col-form" sm="4" md="4" lg="4">
          <s-select-definition
            ref="typeContractRef"
            :def="1069"
            label="Tipo de Contrato"
            v-model="item.TypeContract"
            :add="$fun.isAdmin()"
            :readonly="extension ? extension : renovation"
            :disabled="extension ? extension : renovation"
            @input="onTypeContractChanged($event)"
          />
        </v-col>
        <v-col class="s-col-form" sm="4" md="4" lg="4">
          <s-select-definition
            :def="1482"
            label="Categoria"
            v-model="item.TypeCategory"
            :add="$fun.isAdmin()"
            :readonly="extension ? extension : renovation"
            :disabled="extension ? extension : renovation"
          />
        </v-col>
        <v-col class="s-col-form" sm="4" md="4" lg="4">
          <s-select-definition
            :def="1039"
            :dgr="4"
            label="Tipo de Trabajador"
            v-model="item.TypeWorker"
            :text="item.TypeWorkerName"
            :addCustom = "$fun.isAdmin()"
            :customFields="[
              {
                key: 'DedDescription',
                label: 'Tipo de trabajador',
                required: true,
                cols: 8,
              },
              {
                key: 'DedHelper',
                label: 'Sunat',
                required: true,
                cols: 4,
              },
            ]"
            :readonly="extension ? extension : renovation"
            :disabled="extension ? extension : renovation"
          />
        </v-col>
      </v-row>

      <!-- Sección 5b: Establecimiento y Modalidad Formativa -->
      <v-row>
        <v-col class="s-col-form" sm="4" md="4" lg="4">
          <s-select-establishment
            v-model="item.EstID"
            :disabled="extension ? extension : renovation"
            clearable
          />
        </v-col>
        <v-col class="s-col-form" sm="4" md="4" lg="4" v-if="item.TypeCategory == 4">
          <s-select-definition
            :def="1716"
            label="Modalidad Formativa"
            v-model="item.TypeFormatLabor"
            :disabled="extension ? extension : renovation"
            :add-custom="$fun.isAdmin()"
            :custom-fields="[
              {
                key: 'DedDescription',
                label: 'Modalidad Formativa',
                required: true,
                cols: 9,
              },
              {
                key: 'DedHelper',
                label: 'Código Sunat',
                required: true,
                cols: 3,
              },
            ]"
          />
        </v-col>
      </v-row>

      <!-- Sección 6: Datos laborales complementarios -->
      <v-row>
        <v-col class="s-col-form" sm="4" md="4" lg="4">
          <s-select-definition
            :def="1481"
            label="Régimen laboral"
            v-model="item.TypeLaborRegime"
            :text="item.LaborRegimeText"
            :addCustom = "$fun.isAdmin()"
            :customFields="[
              {
                key: 'DedDescription',
                label: 'Nombre de Régimen',
                required: true,
                cols: 8,
              },
              {
                key: 'DedHelper',
                label: 'Sunat',
                required: true,
                cols: 4,
              },
            ]"
            :readonly="extension ? extension : renovation"
            :disabled="extension ? extension : renovation"
          />
        </v-col>
        <v-col class="s-col-form" sm="4" md="4" lg="4">
          <s-select-definition
            :def="1077"
            label="Cat. Ocupacional"
            v-model="item.TypeOccupationalCategory"
            :add="$fun.isAdmin()"
            :readonly="extension ? extension : renovation"
            :disabled="extension ? extension : renovation"
          />
        </v-col>
      </v-row>

      <!-- Sección 7: Datos personales del contrato -->
      <v-row>
        <v-col class="s-col-form" sm="4" md="4" lg="4">
          <s-select-definition
            :def="1071"
            label="Estado civil"
            v-model="item.TypeCivilStatus"
            :add="$fun.isAdmin()"
            :readonly="extension ? extension : renovation"
            :disabled="extension ? extension : renovation"
          />
        </v-col>
        <v-col class="s-col-form" sm="4" md="4" lg="4">
          <s-text
            label="N° de hijos"
            number
            v-model="item.AgtChildrenQuantity"
            :min="0"
            :readonly="extension ? extension : renovation"
            :disabled="extension ? extension : renovation"
          />
        </v-col>
      </v-row>

      <!-- Sección 8: Remuneración y condiciones -->
      <v-row>
        <v-col class="s-col-form" sm="4" md="4" lg="4">
          <s-protected-admin-field
            ref="salaryProtectedField"
            label="Sueldo/Jornal"
            v-model="item.AgtSalary"
            :encrypted="true"
            :agtID="Number(item.AgtID || 0)"
            :pstID="Number(item.PstID || 0)"
            :canViewByDefault="$fun.isAdmin()"
            :alwaysMasked="true"
            :disabled="extension ? extension : renovation"
            @keyupEnter="$emit('keyupEnter', $event)"
            @keyup.enter="$emit('keyupEnter', $event)"
            @submit="$emit('submit', $event)"
          />
        </v-col>
        <v-col class="s-col-form" sm="4" md="4" lg="4">
          <s-select-definition
            :def="1278"
            label="Turno"
            v-model="item.TypeTurno"
            :add="$fun.isAdmin()"
            :readonly="extension ? extension : renovation"
            :disabled="extension ? extension : renovation"
          />
        </v-col>
        <v-col class="s-col-form" sm="4" md="4" lg="4">
          <s-select-definition
            :def="1045"
            label="Situación"
            v-model="item.TypeSituation"
            :add="$fun.isAdmin()"
            :readonly="extension ? extension : renovation"
            :disabled="extension ? extension : renovation"
          />
        </v-col>
      </v-row>

      <!-- Sección 9: Campaña y situación especial -->
      <v-row>
        <v-col class="s-col-form" sm="4" md="4" lg="4">
          <s-select-campaign-se
            :key="`cmp-${item.CmpID || 0}-${item.TypeSituation || 0}`"
            label="Campaña"
            v-model="item.CmpID"
            ref="CmpIDRef"
            @input="onCampaignSelected"
            @datesLoaded="onCampaignDatesLoaded"
            :disabled="extension ? extension : renovation"
          />
        </v-col>
        <v-col class="s-col-form" sm="4" md="4" lg="4">
          <s-select-definition
            :def="1070"
            label="Situación especial"
            v-model="item.TypeSpecialSituation"
            :add="$fun.isAdmin()"
            :readonly="extension ? extension : renovation"
            :disabled="extension ? extension : renovation"
          />
        </v-col>
        <v-col
          v-if="item.TypeSpecialSituation == 4 || item.TypeSpecialSituation == 5"
          class="s-col-form"
          sm="4"
          md="4"
          lg="4"
        >
          <s-text
            label="Meses de Prueba"
            v-model="item.AgtMonthsProbation"
            type="number"
            :readonly="extension ? extension : renovation"
            :disabled="extension ? extension : renovation"
          />
        </v-col>
      </v-row>

      <!-- Sección 10: Fechas del contrato -->
      <v-row>
        <v-col class="s-col-form" sm="4" md="4" lg="4">
          <s-date
            v-model="item.AgtAdmissionDate"
            label="Fecha de Ingreso"
            :disabled="extension ? extension : renovation"
          />
        </v-col>
        <v-col v-if="renovation || extension" class="s-col-form" sm="4" md="4" lg="4">
          <s-date
            v-model="item.AgtBeginDate"
            label="Inicio de Contrato"
            :disabled="extension"
          />
        </v-col>
        <v-col v-if="!renovation && !extension" class="s-col-form" sm="4" md="4" lg="4">
          <s-text
            label="Inicio de Contrato"
            :value="$fun.formatDateView(item.AgtBeginDate)"
            readonly
            :disabled="extension"
          />
        </v-col>
        <v-col v-if="(renovation || extension) && !item._isIndeterminate" class="s-col-form" sm="4" md="4" lg="4">
          <s-date
            v-model="item.AgtEndDate"
            label="Fin de Contrato"
            :min="MinDay"
          />
        </v-col>
        <v-col v-if="!renovation && !extension && !item._isIndeterminate" class="s-col-form" sm="4" md="4" lg="4">
          <s-text
            label="Fin de Contrato"
            :value="$fun.formatDateView(item.AgtEndDate)"
            readonly
          />
        </v-col>
      </v-row>

      <!-- Sección 11: Opciones adicionales y cese -->
      <v-row>
        <v-col class="s-col-form" xs="4" sm="2" md="4" lg="2">
          <s-switch
            label="Jornada Máxima"
            v-model="item.AgtMaximunDay"
            :disabled="extension ? extension : renovation"
          />
        </v-col>
        <v-col class="s-col-form" sm="2" md="4" lg="2">
          <s-switch
            label="Registrar Cese"
            v-model="RegisterCessation"
            :disabled="extension ? extension : renovation"
          />
        </v-col>
        <v-col class="s-col-form" sm="4" md="3" lg="4" v-if="RegisterCessation">
          <s-select-definition
            ref="terminationRef"
            :def="1051"
            label="Motivo de Cese"
            :clearable="true"
            v-model="item.TypeTermination"
            :add="$fun.isAdmin()"
            :disabled="extension ? extension : renovation"
          />
        </v-col>
        <v-col class="s-col-form" sm="4" md="3" lg="4" v-if="RegisterCessation">
          <s-date
            v-model="item.AgtTerminationDate"
            label="Fecha cese"
            :disabled="extension ? extension : renovation"
          />
        </v-col>
      </v-row>

      <!-- Sección 12: Detalle del servicio (Si aplica) -->
      <v-row v-if="!renovation && !extension && item.TypeContract == 9">
        <v-col cols="12">
          <s-textarea
            label="Describa el Servicio"
            :height="80"
            v-model="item.AgtServiceDescription"
          />
        </v-col>
      </v-row>

      <!-- Sección 13: Fechas del Movimiento (Solo para ediciones normales) -->
      <v-row v-if="!renovation && !extension">
        <v-col class="s-col-form" sm="4" md="3" lg="4">
          <s-date
            v-model="item.AmsStartDateMovement"
            label="Fecha Inicio del Movimiento Actual"
          />
        </v-col>
      </v-row>
      
      <v-row>
        <v-data-table
          :headers="agreementMovemtsHeaders"
          class="elevation-1"          
          :items="modifiedMovements"
          :sort-by="['AmsID','AgtID','SecStatus']" 
          :sort-desc="[true]"
          :group-by="['AgtID']" 
          :group-desc="[true]"
          :items-per-page="4"
        >
        <!-- Slot para la cabecera del grupo 👇 -->
        <template slot="group.header" slot-scope="{ group, headers, isOpen, toggle }">
          <tr>
            <td :colspan="headers.length">
              <v-btn @click="toggle" small icon :class="isOpen ? 'mdi-minus' : 'mdi-plus'"></v-btn>
              <strong>ID de Contrato {{ group }}<br> Inicio {{ $fun.formatDateView(groupByValues[group].AgtBeginDate) }} - Fin {{ $fun.formatDateView(groupByValues[group].AgtEndDate) }}</strong>
            </td>
          </tr>
        </template>
        /<!-- accion para editar -->
        <template v-slot:[`item.AmsID`]="{ item }">
          {{item.AmsID}}
          <v-icon v-if="!extension && !renovation && $fun.isAdmin()"  small @click="dialogOpen(item)">mdi-pencil</v-icon>
        </template>

        <template v-slot:[`item.changes`]="{ item }">
        <div v-if="item.changes">
          <div v-for="(value, key) in item.changes" :key="key">
            <strong>{{ translateField(key) }}:</strong> 
            {{ value.old ? value.old : "Vacío" }} → 
            <span class="text-red">{{ value.new ? value.new : "Vacío" }}</span>
          </div>
        </div>
      </template>

        <!-- Fechas formateadas -->
        <template v-slot:[`item.AmsAdmissionDate`]="{ item }">        
            {{ $fun.formatDateView(item.AmsAdmissionDate) }}          
        </template>
        <template v-slot:[`item.AmsBeginDate`]="{ item }">
          {{ $fun.formatDateView(item.AmsBeginDate) }}    
        </template>  
        <template v-slot:[`item.AmsEndDate`]="{ item }">        
          {{ $fun.formatDateView(item.AmsEndDate) }}        
        </template>
        <template v-slot:[`item.AmsStartDateMovement`]="{ item }">        
          {{ $fun.formatDateView(item.AmsStartDateMovement) }}        
        </template>
        <template v-slot:[`item.AmsEndDateMovement`]="{ item }">        
          {{ $fun.formatDateView(item.AmsEndDateMovement) }}        
        </template>
        <template v-slot:[`item.AmsTerminationDate`]="{ item }">        
          {{ $fun.formatDateView(item.AmsTerminationDate) }}        
        </template>

        <!-- Salario -->
        <template v-slot:[`item.AmsSalary`]="{ item }">        
            {{ canViewSalaryField ? $fun.decryptSalary(item.AmsSalary) : '*****' }}
        </template>
        
        <!-- Estado -->
        <template v-slot:[`item.AmsStatus`]="{ item }">
          <v-chip 
            small 
            :color="item.AmsStatus == 1 ? 'success' : ''" 
            :text-color="item.AmsStatus == 1 ? 'white' : ''" 
            >
            {{ item.AmsStatus == 1  ? 'ACTIVO' : 'INACTIVO' }}
          </v-chip>
        </template>

        <!-- Estado Trabajador -->
        <template v-slot:[`item.AgtStatusName`]="{ item }">
          <v-chip 
            small 
            :color="item.AgtStatusName === 'ACTIVO' ? 'success' : ''" 
            :text-color="item.AgtStatusName === 'ACTIVO' ? 'white' : ''" 
            >
            {{ item.AgtStatusName }}
          </v-chip>
        </template>

        <!-- Máximo Día -->
        <template v-slot:[`item.AmsMaximunDay`]="{ item }">
          <v-chip 
            small 
            :color="item.AmsMaximunDay ? 'success' : ''" 
            :text-color="item.AmsMaximunDay ? 'white' : ''" 
            >
            {{ item.AmsMaximunDay  ? 'SI' : 'NO' }}
          </v-chip>
        </template>
      </v-data-table>
      </v-row>
    </v-card-text>
    <!--<v-card-actions>
        <v-container>
          <v-row>
            <v-col class="s-col-form"><v-btn block text @click="close()">Cancelar</v-btn></v-col>
            <v-col class="s-col-form"><v-btn block color="primary" @click="save()">GUARDAR</v-btn></v-col>
          </v-row>
        </v-container>
      </v-card-actions>-->
    <v-dialog v-model="openDialogMovement" max-width="1000px" persistent>
      <v-card elevation="0">
        <v-card-title>
          <v-row>
            <v-col cols="12">
              <h3 class="headline mb-0">Movimiento de Contrato</h3>
            </v-col>
          </v-row>
        </v-card-title>
        <v-card-text>
      <v-row>
        <v-col class="s-col-form" sm="4" md="4" lg="4">
          <s-select-definition
            :def="1060"
            label="Planilla"
            v-model="movement.TypePayroll"
            :readonly="extension ? extension : renovation"
          />
        </v-col>
        <v-col class="s-col-form" sm="4" md="4" lg="4">
          <s-select-area
            label="Area"
            v-model="movement.AreID"
            :text="movement.AreName"
          ></s-select-area>
        </v-col>
        <v-col class="s-col-form" sm="4" md="4" lg="4">
          <s-select-area
            v-model="movement.CceID"
            :text="movement.CceName"
            centercost
            :clearable="true"
            label="Centro de Costo"
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col class="s-col-form" sm="4" md="4" lg="4">
          <s-select-position
            v-model="movement.PstID"
            :text="movement.PstName"
            centercost
            :clearable="true"
            label="Cargo"
          />
        </v-col>
        <v-col class="s-col-form" sm="4" md="4" lg="4">
          <s-select
            label="Cuadrilla"
            :items="quadrille"
            autocomplete
            clearable
            item-text="GngName"
            item-value="GngID"
            v-model="movement.GngID"
          ></s-select>
        </v-col>
        <v-col class="s-col-form" sm="4" md="4" lg="4">
          <s-select-business-line
            v-model="item.BslID"
            :cceID="item.CceID"
            :clearable="true"
            :item-text="movement.BslName"
            label="Línea de Negocio"
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col class="s-col-form" sm="4" md="4" lg="4">
          <s-select-definition
            :def="1482"
            label="Categoria"
            v-model="movement.TypeCategory"
          />
        </v-col>
        <v-col class="s-col-form" sm="4" md="4" lg="4">
          <s-select-definition
            :def="1069"
            label="Tipo de Contrato"
            v-model="movement.TypeContract"
          />
        </v-col>
        <v-col class="s-col-form" sm="4" md="4" lg="4">
          <s-text
              label="N° de hijos"
              number
              v-model="movement.AmsChildrenQuantity"
              :min="0"
            >
          </s-text>
        </v-col>
      </v-row>
      <v-row v-if="movement.TypeContract == 9">
        <v-col>
          <s-textarea 
          label="Describa el Servicio" 
          :height=80 
          :readonly="extension ? extension : renovation"
          :disabled="extension ? extension : renovation"
          v-model="movement.AmsServiceDescription"
          ></s-textarea>
        </v-col>
      </v-row>
      <v-row>
        <v-col class="s-col-form" sm="4" md="4" lg="4">
          <s-select-definition
            :def="1039"
            :dgr="4"
            label="Tipo de Trabajador"
            v-model="movement.TypeWorker"
          />
        </v-col>
        <v-col class="s-col-form" sm="4" md="4" lg="4">
          <s-select-definition
            :def="1481"
            label="Régimen laboral"
            v-model="movement.TypeLaborRegime"
          />
        </v-col>
        <v-col class="s-col-form" sm="4" md="4" lg="4">
          <s-select-definition
            :def="1077"
            label="Cat. Ocupacional"
            v-model="movement.TypeOccupationalCategory"
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col class="s-col-form" sm="4" md="4" lg="4">          
          <s-select
            label="Profesión"
            :items="profession"
            autocomplete
            clearable
            item-text="PrnDescription"
            item-value="PrnID"
            v-model="movement.PrnID"
          ></s-select>
        </v-col>
        <v-col class="s-col-form" sm="4" md="4" lg="4">          
          <!-- <s-select
            label="Nivel Educativo"
            :items="EducationLevels"
            autocomplete
            item-text="LveDescription"
            item-value="LveID"
            v-model="movement.LveID"
          ></s-select> -->
          <s-select-definition
            :def="1066"
            label="Grado Instrucción"
            v-model="item.LveID"
            :add="$fun.isAdmin()"
          />
        </v-col>
        <v-col class="s-col-form" sm="4" md="4">
          <s-select-definition
            :def="1071"
            label="Estado civil"
            v-model="movement.TypeCivilStatus"
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col class="s-col-form" sm="4" md="4" lg="4">
          <s-select-definition
            :def="1070"
            label="Situación especial"
            v-model="movement.TypeSpecialSituation"
          />
        </v-col>
        <v-col class="s-col-form" sm="4" md="4" lg="4">        
            <s-money-input v-if="canViewSalaryField"  label="Sueldo/Jornal"  v-model="movement.AmsSalary" :readonly="extension ? extension : renovation"> </s-money-input>
          <s-text
              v-else
              label="Sueldo/Jornal"              
              :value="'*****'"
              readonly
            ></s-text>
        </v-col>
        <v-col class="s-col-form" sm="4" md="4" lg="4">
          <s-select-definition
            :def="1278"
            label="Turno"
            v-model="movement.TypeTurno"
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col class="s-col-form" sm="4" md="4" lg="4">
          <s-select-definition
            :def="1045"
            label="Situación"
            v-model="movement.TypeSituation"
          />
          
        </v-col>
        <v-col class="s-col-form" sm="4" md="4" lg="4" v-if="movement.TypeSituation == 2 ? true :false">
          <s-select-campaign-se 
                label="Campaña"
                v-model="movement.CmpID"
              />
        </v-col>
        <v-col v-if="movement.TypeSpecialSituation == 4 || movement.TypeSpecialSituation == 5" class="s-col-form" sm="4" md="4" lg="4">
          <s-text
            label="Meses de Prueba"
            v-model="movement.AmsMonthsProbation"
            type="number"
            :readonly="extension ? extension : renovation"
            :disabled="extension ? extension : renovation"
          />
        </v-col>
      </v-row>      
      <v-row>
        <v-col  v-if="!renovation && !extension " class="s-col-form" sm="4" md="4" lg="4">
          <s-date
            v-model="movement.AmsAdmissionDate"
            label="Fecha de Ingreso"
            :clearable="true"
          />
        </v-col>
        <v-col  v-if="renovation || extension " class="s-col-form" sm="4" md="4" lg="4">
          <s-text 
            label="Fecha de Ingreso"
            :value="$fun.formatDateView(movement.AmsAdmissionDate)"
            >
          </s-text> 
        </v-col>
        <v-col  v-if="!renovation && !extension " class="s-col-form" sm="4" md="4" lg="4">
          <s-date
            v-model="movement.AmsBeginDate"
            label="Inicio de Contrato"
            :clearable="true"
          />
        </v-col>
        <v-col  v-if="renovation || extension " class="s-col-form" sm="4" md="4" lg="4">
          <s-text 
            label="Inicio de Contrato"
            :value="$fun.formatDateView(movement.AmsBeginDate)"
            >
          </s-text> 
        </v-col>
        <v-col v-if="!renovation && !extension " class="s-col-form" sm="4" md="4" lg="4">
          <s-date
            v-model="movement.AmsEndDate"
            label="Fin de Contrato"
            :clearable="true"
          />
        </v-col>
        <v-col v-if="renovation || extension " class="s-col-form" sm="4" md="4" lg="4">
          <s-text 
            label="Fin de Contrato"
            :value="$fun.formatDateView(movement.AmsEndDate)"
            >
          </s-text> 
        </v-col>
        
      </v-row>
      <v-row>
        <v-col
          class="s-col-form "
          xs="4"
          sm="2"
          md="4"
          lg="2"
        >
          <s-switch
            label="Jornada Máxima"
            v-model="movement.AmsMaximunDay"
          ></s-switch>
        </v-col>
        <v-col class="s-col-form " sm="2" md="4" lg="2">
          <v-switch
            label="Registrar Cese"
            v-model="sRegisterCessationMovement"
            style="margin-top: 0px;"
          />
        </v-col>

        <v-col class="s-col-form" sm="4" md="3" lg="4" v-if="sRegisterCessationMovement">
          <s-select-definition
            :def="1051"
            label="Motivo de Cese"
            :clearable="true"
            v-model="movement.TypeTermination"
          />
        </v-col>
        <v-col class="s-col-form" sm="4" md="3" lg="4" v-if="sRegisterCessationMovement">
          <s-date
            v-model="movement.AmsTerminationDate"
            label="Fecha cese"
          />
        </v-col>
      </v-row>
        </v-card-text>
        <v-card-actions>
          <v-btn color="primary" @click="saveMovement()">Guardar</v-btn>
          <v-btn color="error" @click="closeDialog()">Cancelar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </v-card>
</template>

<script>
import SSelectArea from "../../../components/Utils/SSelectArea.vue";
import _sQuadriService from "@/services/HumanResource/hmnGang.js";
import _sProfService from "@/services/HumanResource/ProfessionsGen.js";
import SSelectBusinessLine from "../../../components/Utils/SSelectBusinessLine.vue";
import _sCttoService from "@/services/HumanResource/HmnContractService.js";
import _sHmnAgreementMovemt from "@/services/HumanResource/HmnAgreementMovemt.js";
import sToolbarPerson from "@/components/Utils/RecursosHm/sToolbarAgreementPerson";
import sEducationLevel from "@/services/HumanResource/HmnEducationalLevel.js";
import SSelectPosition from "../../../components/Utils/sSelectPosition.vue";
import _constapp from "@/plugins/const";
import _sPositionService from "@/services/General/PositionService.js";
import SProtectedAdminField from "@/components/Utils/RecursosHm/sProtectedAdminField.vue";
import SSelectDefinition from "../SSelectDefinition.vue";
import SSelectEstablishment from "../../../components/Utils/SSelectEstablishment.vue";
import SSelectEducationalInstitution from "../../../components/Utils/SSelectEducationalInstitution.vue";
import SSelectEducationalMajor from "../../../components/Utils/SSelectEducationalMajor.vue";
import _sRecruitmentAccess from "@/services/HumanResource/RecruitmentAccessService";

export default {
  name: "sDataLaboral",
  components: {    
    SSelectArea,
    SSelectBusinessLine,
    sToolbarPerson,
    SSelectPosition,
    SProtectedAdminField,
    SSelectDefinition,
    SSelectEstablishment,
    SSelectEducationalInstitution,
    SSelectEducationalMajor,
  },
  props: {
    visibleTitle: {
      type: Boolean,
      default: true,
    },
    full: {
      type: Boolean,
      default: false,
    },
    typeperson: {
      type: Number,
      default: 1,
    },
    addTypeSex: {
      type: Boolean,
      default: false,
    },
    addBirthDate: {
      type: Boolean,
      default: false,
    },
    addDriver: {
      type: Boolean,
      default: false,
    },
    datawork: { type: Object },

    extension: {
      type: Boolean,
      default: false,
    },
    renovation: {
      type: Boolean,
      default: false,
    },

  },
  data() {
    return {
      dataWor: {},
      profession: [],
      EducationLevels: [],
      quadrille: [],
      Position: [],
      editing: false,
      item: {
        TypePayroll: null,
        AreID: null,
        // AreName: "",
        // CceName: "",
        // PstName:"",
        PstID: null,
        CmpID: null,
        BslID: null,
        TypeWorker: null,
        PrnID: null,
        GngID: null,
        TypeSituation: null,
        AgtAdmissionDate: null,
        AgtBeginDate: null,
        AgtEndDate: null,
        LveID: null,
        TypeSpecialSituation: null,
        TypeTermination: null,
        TypeCivilStatus: null,
        AgtTerminationDate: null,
        TypeLaborRegime: null,
        //RegisterCessation: false,
        centercost: null,
        TypeCategory: null,
        TypeContract: null,
        TypeTurno: null,
        TypeOccupationalCategory: null,
        AgtSalary: null,
        AgtChildrenQuantity: null,
        AgtStatus: null,
        ReqID: null,
        PrsDocumentNumber: null,
        CceID: 0,
        AgtMaximunDay:null,
        AgtRenovation:null,
        AgtMonthsProbation:null,
        AgreementMovemtsCE:[],
        UsrUpdateID:this.$fun.getUserID(),
        AmsStartDateMovement: null,
        AmsEndDateMovement: null,
        MinDay: null,
        PstIsBascCritic: 0,
        PstIsBascCriticName:"",
      },
      sourceLabel: null,
      sourceRequirements: [],
      assignedRequirementIds: [],
      vacancyStatus: {
        TotalVacancies: 0,
        CoveredVacancies: 0,
        ActiveContracts: 0
      },
      selectedSourceRequirementKey: null,
      selectedCenterCost: 0,
      selectedBusinessLine: null,
      menu3: false,
      hasPermissionSeeSalary:false,
      config: {},
      dialog: false,
      RegisterCessation:null,
      sRegisterCessationMovement: false,
      sourceHydratedDocumentNumber: null,
      isSyncingContractDates: false,
      isLoadingRequirementData: false,
      agreementMovemtsHeaders: [ 
        //accion
        //{ text: "Acciones"                      , value: "actions"                        , sortable: false   , width: 30   },
        { text: "ID de Movimiento"                , value: "AmsID"                          , sortable: false   , width: 220,  },
        { text: "ID de Contrato"                  , value: "AgtID"                          , sortable: false   , width: 250, },
        // { text: "Incio de Contrato"               , value: "AgtBeginDate"                   , sortable: false   , width: 20,  },
        // { text: "Fin de Contrato"                 , value: "AgtEndDate"                     , sortable: false   , width: 20,  },       
        { text: "Fecha de Ingreso"                , value: "AmsAdmissionDate"               , sortable: false   , width: 80,  },
        { text: "Fecha de Inicio de Movimiento"   , value: "AmsStartDateMovement"           , sortable: false   , width: 80,  },
        { text: "Fecha de Fin de Movimiento"      , value: "AmsEndDateMovement"             , sortable: false   , width: 80,  }, 
        { text: "Estado del Trabajador"           , value: "AgtStatusName"                  , sortable: false   , width: 80,  },       
        { text: "Tipo de Movimiento"              , value: "TypeAmsName"                    , sortable: false   , width: 80,  }, 
        { text: "Estado del Movimiento"           , value: "AmsStatus"                      , sortable: false   , width: 80,  }, 
        { text: "Campos Modificados"              , value: "changes"                        , sortable: false   , width: 350, },
        { text: "Tipo de Planilla"                , value: "TypePayrollName"                , sortable: false   , width: 100, },
        { text: "Área"                            , value: "AreName"                        , sortable: false   , width: 100, },
        { text: "Centro de Costo"                 , value: "CceName"                        , sortable: false   , width: 100, },
        { text: "Cargo"                           , value: "PstName"                        , sortable: false   , width: 100, },
        { text: "Cuadrilla"                       , value: "GngName"                        , sortable: false   , width: 100, },
        { text: "Línea de Negocio"                , value: "BslName"                        , sortable: false   , width: 80,  },
        { text: "Campaña"                         , value: "CmpName"                        , sortable: false   , width: 80,  },
        { text: "Categoria"                       , value: "TypeCategoryName"               , sortable: false   , width: 80,  },
        { text: "Tipo de Contrato"                , value: "TypeContractName"               , sortable: false   , width: 80,  },
        { text: "Tipo de Trabajador"              , value: "TypeWorkerName"                 , sortable: false   , width: 80,  },
        { text: "Régimen Laboral"                 , value: "TypeLaborRegimeName"            , sortable: false   , width: 80,  },
        { text: "Categoria Ocupacional"           , value: "TypeOccupationalCategoryName"   , sortable: false   , width: 80,  },
        { text: "Profesión"                       , value: "PrnDescription"                 , sortable: false   , width: 80,  },
        { text: "Nivel Educativo"                 , value: "LveDescription"                 , sortable: false   , width: 80,  },
        { text: "Estado Civil"                    , value: "TypeCivilStatusName"            , sortable: false   , width: 80,  },
        { text: "Situación"                       , value: "TypeSituationName"              , sortable: false   , width: 80,  },
        { text: "Situación Especial"              , value: "TypeSpecialSituationName"       , sortable: false   , width: 80,  },
        { text: "Sueldo/Jornal"                   , value: "AmsSalary"                      , sortable: false   , width: 80,  },
        { text: "N° de Hijos"                     , value: "AmsChildrenQuantity"            , sortable: false   , width: 80,  },
        //{ text: "Estado del Trabajador"           , value: "AmsStatusName"                  , sortable: false   , width: 80,  },
        { text: "Turno"                           , value: "TypeTurnoName"                  , sortable: false   , width: 80,  },
        { text: "Descripcion del Servicio"        , value: "AmsServiceDescription"                    , sortable: false   , width: 80,  },
        { text: "Meses De Prueba"                 , value: "AmsMonthsProbation"               , sortable: false   , width: 80,  },
        //{ text: "Fecha de Ingreso"                , value: "AmsAdmissionDate"               , sortable: false   , width: 80,  },
        { text: "Inicio de Contrato"              , value: "AmsBeginDate"                   , sortable: false   , width: 80,  },
        { text: "Fin de Contrato"                 , value: "AmsEndDate"                     , sortable: false   , width: 80,  },
        { text: "Jornada Máxima"                  , value: "AmsMaximunDay"                  , sortable: false   , width: 80,  },
        { text: "Motivo de Cese"                  , value: "TypeTerminationName"            , sortable: false   , width: 80,  },
        { text: "Fecha de Cese"                   , value: "AmsTerminationDate"             , sortable: false   , width: 80,  },          
        // { text: "Estado"                      , value: "SecStatus"                      , sortable: false   , width: 30   },
        // { text: "Acciones"                    , value: "actions"                        , sortable: false   , width: 30   },
      ],
      fieldTranslations: {
        AmsID: "ID Movimiento",
        AgtID: "ID Contrato",
        AmsAdmissionDate: "Fecha de Ingreso",
        AmsStartDateMovement: "Fecha Inicio del Movimiento",
        AmsEndDateMovement: "Fecha Fin del Movimiento",       
        TypeAmsName: "Tipo de Movimiento",       
        TypePayrollName: "Tipo de Planilla",
        AreName: "Área",
        CceName: "Centro de Costo",
        PstName: "Cargo",
        GngName: "Cuadrilla",
        BslName: "Línea de Negocio",
        CmpName: "Campaña",
        TypeCategoryName: "Categoria",
        TypeContractName: "Tipo de Contrato",
        TypeWorkerName: "Tipo de Trabajador",
        TypeLaborRegimeName: "Régimen Laboral",
        TypeOccupationalCategoryName: "Categoria Ocupacional",
        PrnDescription: "Profesión",
        LveDescription: "Nivel Educativo",
        TypeCivilStatusName: "Estado Civil",
        TypeSituationName: "Situación",
        TypeSpecialSituationName: "Situación Especial",
        AmsSalary: "Sueldo/Jornal",
        AmsChildrenQuantity: "N° de Hijos",
        TypeTurnoName: "Turno",
        AmsServiceDescription: "Descripcion del Servicio",
        AmsMonthsProbation: "Meses De Prueba",
        AmsBeginDate: "Inicio de Contrato",
        AmsEndDate: "Fin de Contrato",
        AmsMaximunDay: "Jornada Máxima",
        TypeTerminationName: "Motivo de Cese",
        AmsTerminationDate: "Fecha de Cese",
        AmsMaximunDayName: "Jornada Máxima",
        AgtStatusName: "Estado del Trabajador",
        // AmsSalary: "Salario",
        // StartDate: "Fecha Inicio",
        // EndDate: "Fecha Fin",
        // TypeAmsName : "Tipo de Contrato",
        // AmsTerminationDate: "Fecha de Cese"   ,

      },
      agreementMovemtsDetail:[],
      modifiedMovements: [], // Aquí guardaremos los cambios detectados
      groupByValues: {}, // Objeto para almacenar los valores de AgtBeginDate y AgtEndDate por AgtID
      openDialogMovement: false,
      salaryAdminUnlocked: false,
      salaryHierarchyAllowed: false,
      salaryCanShowValue: false,
      movement: {
        AmsID: null,
        AgtID: null,
        AmsAdmissionDate: null,
        AmsBeginDate: null,
        AmsEndDate: null,
        AmsStartDateMovement: null,
        AmsEndDateMovement: null,
        AmsTerminationDate: null,
        SecStatus: null,
        TypeAmsName: null,
        AmsStatus: null,
        AmsStatusName: null,
        TypePayroll: null,
        AreID: null,
        AreName: null,
        CceName: null,
        PstName: null,
        PstID: null,
        CceID: null,
        CmpID: null,
        BslID: null,
        TypeCategory: null,
        TypeContract: null,
        TypeWorker: null,
        AmsMonthsProbation: null,
        TypeLaborRegime: null,
        TypeOccupationalCategory: null,
        LveID: null,
        TypeCivilStatus: null,
        TypeSituation: null,
        TypeSpecialSituation: null,
        AgtChildrenQuantity: null,
        TypeTurno: null,
        AgtStatus: null,
        PrnID: null,
        GngID: null,
        AmsMaximunDay: null,
        AmsRenovation: null,
        ReqID: null,
        UsrUpdateID: null,
        RegisterCessation: null,
      },
    };
  },
  inject: ['contractDates'], // Recibe las fechas del padre
  computed: {
    hasProjections() {
      return !!this.vacancyStatus?.ProjectionsJSON;
    },
    parsedProjections() {
      if (!this.vacancyStatus?.ProjectionsJSON) return [];
      try {
        return JSON.parse(this.vacancyStatus.ProjectionsJSON);
      } catch (e) {
        return [];
      }
    },
    canViewSalaryField() {
      return this.$fun.isAdmin() || this.salaryHierarchyAllowed || this.salaryAdminUnlocked;
    },
    hasSourceRequirements() {
      return this.sourceRequirementItems.length > 0;
    },
    sourceRequirementItems() {
      let filtered = this.sourceRequirements || [];
      if (!this.$fun.isAdmin() && !this.$fun.specialpermission) {
        const currentUserId = this.$fun.getUserID();
        filtered = filtered.filter((req) => {
          if (!req.AssignedUserIDs) return true;
          const userIds = req.AssignedUserIDs.split(',').map(Number);
          return userIds.includes(currentUserId);
        });
      }
      return filtered.map((requirement) => ({
        text: this.buildSourceRequirementLabel(requirement),
        value: this.buildSourceRequirementKey(requirement),
      }));
    },
    isFieldsDisabled() {
      return this.extension || this.renovation;
    },
    isPrimaryFieldDisabled() {
      if (this.$fun.isAdmin()) {
        return false;
      }
      if (this.sourceLabel === "Anterior" || this.sourceLabel === "Personal" || this.sourceLabel === "Contrato anterior") {
        return false;
      }
      return !!this.item.ReqID || this.isFieldsDisabled;
    },
  },
  methods: {
    onSalaryAccessChanged(payload) {
      this.salaryHierarchyAllowed = !!payload?.hasHierarchyAccess;
      this.salaryCanShowValue = !!payload?.canPersistValue;
    },

    logTrace(step, payload = null) {
      // console.log silenciosos en producción para mejorar la performance
    },
    getFieldValue(source, fieldName) {
      if (!source) {
        return null;
      }

      if (source[fieldName] !== undefined) {
        return source[fieldName];
      }

      const camelCaseField =
        fieldName.charAt(0).toLowerCase() + fieldName.slice(1);

      if (source[camelCaseField] !== undefined) {
        return source[camelCaseField];
      }

      return null;
    },
    hasValue(value) {
      return value !== null && value !== undefined && value !== "";
    },
    refreshHydratedSelectors() {
      this.$nextTick(() => {
        const areaLoad = this.$refs.areaRef?.$refs?.sarea?.load;
        const centerLoad = this.$refs.centerCostRef?.$refs?.sarea?.load;
        const positionLoad = this.$refs.positionRef?.$refs?.generic?.load;

        if (typeof areaLoad === "function") {
          areaLoad.call(this.$refs.areaRef.$refs.sarea);
        }

        if (typeof centerLoad === "function") {
          centerLoad.call(this.$refs.centerCostRef.$refs.sarea);
        }

        if (typeof positionLoad === "function") {
          positionLoad.call(this.$refs.positionRef.$refs.generic);
        }
      });
    },
    buildSourceRequirementKey(requirement) {
      if (!requirement) {
        return null;
      }

      return [
        requirement.ReqID || 0,
        requirement.PstID || 0,
        requirement.CceID || 0,
        requirement.CmpID || 0,
      ].join(":");
    },
    buildSourceRequirementLabel(requirement) {
      const parts = [];

      if (this.hasValue(requirement?.ReqID)) {
        parts.push(`REQ ${requirement.ReqID}`);
      }
      if (this.hasValue(requirement?.PstName)) {
        parts.push(requirement.PstName);
      }
      if (this.hasValue(requirement?.AreName)) {
        parts.push(requirement.AreName);
      }
      if (this.hasValue(requirement?.CceName)) {
        parts.push(requirement.CceName);
      }

      return parts.join(" | ");
    },
    normalizeContractSourceRequirements(contractSource) {
      const positionsRaw =
        this.getFieldValue(contractSource, "Positions") ||
        this.getFieldValue(contractSource, "positions") ||
        [];

      const positions = Array.isArray(positionsRaw)
        ? positionsRaw
            .map((position) => ({
              ReqID: this.getFieldValue(position, "ReqID"),
              AreID: this.getFieldValue(position, "AreID"),
              AreName: this.getFieldValue(position, "AreName"),
              CceID: this.getFieldValue(position, "CceID"),
              CceName: this.getFieldValue(position, "CceName"),
              PstID: this.getFieldValue(position, "PstID"),
              PstName: this.getFieldValue(position, "PstName"),
              CmpID: this.getFieldValue(position, "CmpID"),
              ReqEstBudget: this.getFieldValue(position, "ReqEstBudget"),
              ReqEndContract: this.getFieldValue(position, "ReqEndContract"),
            }))
            .filter(
              (position) =>
                this.hasValue(position?.ReqID) ||
                this.hasValue(position?.PstID) ||
                this.hasValue(position?.AreID) ||
                this.hasValue(position?.CceID)
            )
        : [];

      if (positions.length > 0) {
        return positions;
      }

      const singleRequirement = {
        ReqID: this.getFieldValue(contractSource, "ReqID"),
        AreID: this.getFieldValue(contractSource, "AreID"),
        AreName: this.getFieldValue(contractSource, "AreName"),
        CceID: this.getFieldValue(contractSource, "CceID"),
        CceName: this.getFieldValue(contractSource, "CceName"),
        PstID: this.getFieldValue(contractSource, "PstID"),
        PstName: this.getFieldValue(contractSource, "PstName"),
        CmpID: this.getFieldValue(contractSource, "CmpID"),
        ReqEstBudget: this.getFieldValue(contractSource, "ReqEstBudget"),
        ReqEndContract: this.getFieldValue(contractSource, "ReqEndContract"),
      };

      if (
        !this.hasValue(singleRequirement.ReqID) &&
        !this.hasValue(singleRequirement.PstID) &&
        !this.hasValue(singleRequirement.AreID) &&
        !this.hasValue(singleRequirement.CceID)
      ) {
        return [];
      }

      return [singleRequirement];
    },
    findSourceRequirementByKey(requirementKey) {
      return this.sourceRequirements.find(
        (requirement) =>
          this.buildSourceRequirementKey(requirement) === requirementKey
      );
    },
    applyRequirementData(requirement) {
      if (!requirement) {
        return;
      }

      this.isLoadingRequirementData = true;

      this.item.ReqID = this.hasValue(requirement.ReqID)
        ? requirement.ReqID
        : null;

      if (this.hasValue(requirement.AreID)) {
        this.item.AreID = requirement.AreID;
      }
      if (this.hasValue(requirement.AreName)) {
        this.item.AreName = requirement.AreName;
      }
      if (this.hasValue(requirement.CceID)) {
        this.selectedCenterCost = requirement.CceID;
        this.item.CceID = requirement.CceID;
      }
      if (this.hasValue(requirement.CceName)) {
        this.item.CceName = requirement.CceName;
      }
      if (this.hasValue(requirement.CmpID)) {
        this.item.CmpID = requirement.CmpID;
        this.item.TypeSituation = 2;
      }
      let salaryResolved = 0;
      let hasValidSalary = false;

      if (requirement.ReqEstBudget) {
        let budget = null;
        try {
          budget = this.$fun.decrypt(requirement.ReqEstBudget);
        } catch (e) {
          budget = null;
        }
        
        if (budget && !isNaN(budget) && Number(budget) > 0) {
          salaryResolved = budget;
          hasValidSalary = true;
        } else if (requirement.ReqEstBudget && !isNaN(requirement.ReqEstBudget) && Number(requirement.ReqEstBudget) > 0) {
          salaryResolved = requirement.ReqEstBudget;
          hasValidSalary = true;
        } else if (requirement.ReqEstBudget === "FALTANTE") {
          salaryResolved = "FALTANTE";
          hasValidSalary = true;
        } else if (requirement.ReqEstBudget && typeof requirement.ReqEstBudget === "string" && requirement.ReqEstBudget.trim().length > 0) {
          salaryResolved = requirement.ReqEstBudget.trim();
          hasValidSalary = true;
        }
      }

      if (hasValidSalary) {
        this.item.AgtSalary = salaryResolved;
      } else {
        this.item.AgtSalary = 0;
        this.$fun.alert("El requerimiento seleccionado no cuenta con un sueldo de monto fijo establecido. Se asignará 0.00 temporalmente.", "warning");
      }

      const requirementPstID = requirement.PstID;
      const requirementPstName = requirement.PstName;

      this.$nextTick(() => {
        if (this.hasValue(requirementPstID)) {
          this.item.PstID = requirementPstID;
        }
        if (this.hasValue(requirementPstName)) {
          this.item.PstName = requirementPstName;
        }

        this.refreshHydratedSelectors();

        setTimeout(() => {
          this.isLoadingRequirementData = false;
        }, 300);
      });
    },
    handleSourceRequirementSelection(requirementKey) {
      const requirement = this.findSourceRequirementByKey(requirementKey);
      if (!requirement) {
        return;
      }

      this.applyRequirementData(requirement);
    },
    applyContractSource(contractSource) {
      if (!contractSource) {
        return;
      }

      this.sourceHydratedDocumentNumber =
        this.getFieldValue(contractSource, "PrsDocumentNumber") || null;
      this.sourceLabel = this.getFieldValue(contractSource, "SourceLabel") || null;
      this.sourceRequirements = this.normalizeContractSourceRequirements(contractSource);

      const currentRequirementKey = this.buildSourceRequirementKey({
        ReqID: this.item.ReqID,
        PstID: this.item.PstID,
        CceID: this.item.CceID,
        CmpID: this.item.CmpID,
      });

      const defaultRequirement = (currentRequirementKey
        ? this.findSourceRequirementByKey(currentRequirementKey)
        : null) || (this.sourceRequirements.length > 0 ? this.sourceRequirements[0] : null);

      if (defaultRequirement) {
        this.selectedSourceRequirementKey = this.buildSourceRequirementKey(defaultRequirement);
        this.applyRequirementData(defaultRequirement);
      } else {
        this.selectedSourceRequirementKey = null;
      }
    },
    async loadContractSourceByDocument(prsDocumentNumber) {
      if (!prsDocumentNumber) {
        this.sourceLabel = null;
        this.sourceRequirements = [];
        this.selectedSourceRequirementKey = null;
        this.sourceHydratedDocumentNumber = null;
        return;
      }

      try {
        const response = await _sCttoService.contractSourceByDocument(prsDocumentNumber);
        const source = response?.data || null;
        if (!source) {
          this.sourceLabel = null;
          this.sourceRequirements = [];
          this.selectedSourceRequirementKey = null;
          return;
        }

        this.applyContractSource(source);
      } catch (error) {
        this.logTrace("loadContractSourceByDocument.error", error);
      }
    },
    dialogOpen(item) {
      this.movement = { ...item };
      this.sRegisterCessationMovement = !!item.AmsTerminationDate;
      this.movement.AmsSalary = this.canViewSalaryField ? this.$fun.decryptSalary(item.AmsSalary) : item.AmsSalary;
      this.openDialogMovement = true;
      this.$nextTick(() => {
        this.sRegisterCessationMovement = !!item.AmsTerminationDate;
      });
    },
    saveMovement(){
      this.$fun.alert('Seguro de guardar movimiento?', 'question')
			.then(resp => {
				if(resp.value){
					this.movement.AmsSalary = isNaN(this.movement.AmsSalary) ? this.movement.AmsSalary : this.$fun.encryptSalary(this.movement.AmsSalary);  
      this.movement.AmsMonthsProbation = this.movement.TypeSpecialSituation == 4 || this.movement.TypeSpecialSituation == 5 ? (this.movement.AmsMonthsProbation == 0 ? 3 :this.movement.AmsMonthsProbation) : 0;    
      this.movement.AmsServiceDescription = this.movement.TypeContract == 9 ? this.movement.AmsServiceDescription : null;
      //si es un movimiento de cese
      if(this.RegisterCessation){
        this.movement.AmsTerminationDate = this.movement.AmsTerminationDate ? this.movement.AmsTerminationDate : null
        this.movement.AmsEndDateMovement = this.movement.AmsTerminationDate;
        this.movement.AmsStatus = 0;
        this.movement.AgtStatus = 2;
      }else{
        this.movement.AmsTerminationDate = null;
        // el endatemovement es igual al enddate
        this.movement.AmsEndDateMovement = this.movement.AmsEndDate;
        this.movement.AmsStatus = 1;
        this.movement.AgtStatus = 1;
      }
      _sHmnAgreementMovemt.save(
        this.movement, this.$fun.getUserID()
      ).then(() => {        
        //secStatus == 1 es activo o si es el ultimo movimiento de todos en el contrato
        //sacar el ultimo movimiento de todos los movimientos
        let islast = this.agreementMovemtsDetail[this.agreementMovemtsDetail.length-1].AmsID == this.movement.AmsID;
        //console.log("islast",islast);
        
        if(this.movement.SecStatus == 1 || islast){
          //refrescar los datos de item 
          this.item.AgtEndDate = this.movement.AmsEndDate;
          this.item.AgtRenovation = this.movement.AmsRenovation;
          this.item.AgtMaximunDay = this.movement.AmsMaximunDay;
          this.item.AgtStatus = this.movement.AmsStatus;
          this.item.AgtTerminationDate = this.movement.AmsTerminationDate;
          this.RegisterCessation = this.movement.AmsTerminationDate ? true : false;
          this.item.AgtBeginDate = this.movement.AmsBeginDate;
          this.item.AgtAdmissionDate = this.movement.AmsAdmissionDate;
          this.item.AgtSalary = this.canViewSalaryField ? this.$fun.decryptSalary(this.movement.AmsSalary) : this.movement.AmsSalary;
          this.item.AgtChildrenQuantity = this.movement.AmsChildrenQuantity;
          this.item.TypeTurno = this.movement.TypeTurno;
          this.item.TypeSituation = this.movement.TypeSituation;
          this.item.TypeSpecialSituation = this.movement.TypeSpecialSituation;
          this.item.TypeCivilStatus = this.movement.TypeCivilStatus;
          this.item.TypeOccupationalCategory = this.movement.TypeOccupationalCategory;
          this.item.TypeLaborRegime = this.movement.TypeLaborRegime;
          this.item.TypeWorker = this.movement.TypeWorker;
          this.item.TypeContract = this.movement.TypeContract;
          this.item.TypeCategory = this.movement.TypeCategory;
          this.item.BslID = this.movement.BslID;
          this.selectedBusinessLine = this.movement.BslID;
          this.item.CmpID = this.movement.CmpID;
          this.item.GngID = this.movement.GngID;
          this.item.PstID = this.movement.PstID;
          this.item.CceID = this.movement.CceID;
          this.selectedCenterCost = this.movement.CceID;
          this.item.AreID = this.movement.AreID;
          this.item.ReqID = this.movement.ReqID || this.item.ReqID || null;
          this.item.TypePayroll = this.movement.TypePayroll;
          this.item.UsrUpdateID = this.$fun.getUserID();    
        }
        this.closeDialog();
        this.$fun.alert('Actualizado correctamente','success')
      }).catch((e)=>{
        console.log("error",e);
        this.$fun.alert(e,'error')
      }).finally(()=>{
        //cerrar modal de edicion de contrato
        this.clear();
        this.$emit("closeModals");
      });
				}
			});
      
    },
    closeDialog(){
      this.movement = {};
      this.openDialogMovement = false;
    },
    fetchVacancyStatus(reqID) {
      _sCttoService.requirementVacancyStatus(reqID, this.$fun.getUserID())
        .then((resp) => {
          if (resp.status === 200 && resp.data) {
            this.vacancyStatus = resp.data;
          }
        })
        .catch((error) => {
          console.error("Error al obtener estatus de vacantes:", error);
        });
    },
    initialize() {
      this.getProfession();
      this.getQuadrille();
      this.getEducationLevels();
      //this.getPositiom();
      
        //console.log("DATAWORK", this.datawork);
       

        this.agreementMovemtsDetail = this.datawork.AgreementCE.AgreementMovemtsCE;
        this.getModifiedFields(this.agreementMovemtsDetail);
        // Crear una copia sin AgreementMovemtsCE
        const { AgreementMovemtsCE, ...rest } = this.datawork.AgreementCE;
        

        this.item = { ...rest };
        //console.log("AgtSalary",this.item.AgtSalary);
        this.item.AgtSalary = this.canViewSalaryField ?  this.$fun.decryptSalary(this.item.AgtSalary) : this.item.AgtSalary;
        this.item.ReqID = this.item.ReqID || null;
        this.selectedCenterCost = this.item.CceID || 0;
        this.selectedBusinessLine = this.item.BslID || null;
        this.MinDay = this.item.AgtEndDate;
        this.$nextTick(() => {
          this.updateContractEndDate();
        });
        // this.item = { ...this.datawork.AgreementCE };

        // this.item.TypePayroll                 = this.datawork.AgreementCE.TypePayroll 
        // this.item.AreName                     = this.datawork.AgreementCE.AreName 
        // this.item.AreID                       = this.datawork.AgreementCE.AreID   
        // this.item.CceName                     = this.datawork.AgreementCE.CceName 
        // this.item.CceID                       = this.datawork.AgreementCE.CceID   
        // this.item.PstName                     = this.datawork.AgreementCE.PstName 
        // this.item.PstID                       = this.datawork.AgreementCE.PstID   
        // this.item.GngID                       = this.datawork.AgreementCE.GngID   
        // this.item.BslID                       = this.datawork.AgreementCE.BslID   
        // this.item.BslName                     = this.datawork.AgreementCE.BslName 
        // this.item.CmpID                       = this.datawork.AgreementCE.CmpID   
        // this.item.TypeCategory                = this.datawork.AgreementCE.TypeCategory 
        // this.item.TypeContract                = this.datawork.AgreementCE.TypeContract
        // this.item.TypeWorker                  = this.datawork.AgreementCE.TypeWorker
        // this.item.TypeLaborRegime             = this.datawork.AgreementCE.TypeLaborRegimeg
        // this.item.TypeOccupationalCategory    = this.datawork.AgreementCE.TypeOccupationalCategory 
        // this.item.LveID                       = this.datawork.AgreementCE.LveID
        // this.item.TypeCivilStatus             = this.datawork.AgreementCE.TypeCivilStatus
        // this.item.TypeSituation               = this.datawork.AgreementCE.TypeSituation
        // this.item.TypeSpecialSituation        = this.datawork.AgreementCE.TypeSpecialSituation
        // this.item.AgtSalary                   = this.datawork.AgreementCE.AgtSalary
        // this.item.AgtChildrenQuantity         = this.datawork.AgreementCE.AgtChildrenQuantity
        // this.item.AgtStatus                   = this.datawork.AgreementCE.AgtStatus
        // this.item.TypeTurno                   = this.datawork.AgreementCE.TypeTurno
        // this.item.AgtAdmissionDate            = this.datawork.AgreementCE.AgtAdmissionDate
        // this.item.AgtBeginDate                = this.datawork.AgreementCE.AgtBeginDate
        // this.item.AgtEndDate                  = this.datawork.AgreementCE.AgtEndDate     
        // this.item.AgtMaximunDay               = this.datawork.AgreementCE.AgtMaximunDay
        // this.item.TypeTermination             = this.datawork.AgreementCE.TypeTermination
        // this.item.AgtTerminationDate          = this.datawork.AgreementCE.AgtTerminationDate
        // this.item.AgtRenovation               = this.datawork.AgreementCE.AgtRenovation, // Renovacion
        // this.item.RqpID                       = this.datawork.AgreementCE.RqpID, //requerimiento de Personal

        // this.item.SecStatus                   = this.datawork.AgreementCE.SecStatus
        // this.item.UsrCreateID                 = this.datawork.AgreementCE.UsrCreateID
        
        // this.item.RegisterCessation = !!this.datawork.AgreementCE.AgtTerminationDate; // Booleano
        // this.item.AgtMaximunDay = this.datawork.AgtMaximunDay || false;

        this.RegisterCessation                = this.datawork.AgreementCE.AgtTerminationDate ? 1 : false;

        this.item.UsrUpdateID                 = this.$fun.getUserID(),
        this.item.NtpID                       = this.datawork.NtpID  //Persona de Contrato
        this.item.PrsDocumentNumber           = this.datawork.PrsDocumentNumber
        this.loadContractSourceByDocument(this.item.PrsDocumentNumber);
        // this.item.AgreementMovemtsCE[0].AmsStatus       = 0
        // this.item.AgreementMovemtsCE[0].UsrUpdateID     = this.$fun.getUserID(),
        this.prepareGroupByValues();
      //console.log("itemm",this.item);
    },

    translateField(field) {
      return this.fieldTranslations[field] || field;
    },

    getModifiedFields(movements) {
      let sortedMovements = [...movements].sort((a, b) => a.AmsID - b.AmsID);
      let modified = [];

      for (let i = 0; i < sortedMovements.length; i++) {
        let current = sortedMovements[i];
        let previous = sortedMovements[i - 1]; // Movimiento anterior

        let changes = {};

        // Solo comparamos si hay un anterior del mismo contrato
        if (previous && previous.AgtID === current.AgtID) {
          const excludeFields = [
            "TypeTermination", 
            "AgtID", 
            "TypeAms",
            "AmsStartDateMovement",
            "AmsEndDateMovement",
            "AmsStatus",
            "AmsID",
            "SecStatus",
            "PrnID",
            "AreID",
            "CceID",
            "BslID",
            "PstID",
            "GngID",
            "CmpID",
            "TypePayroll",
            "TypeCategory",
            "TypeContract",
            "TypeWorker",
            "TypeLaborRegime",
            "TypeOccupationalCategory",
            "LveID",
            "TypeCivilStatus",
            "TypeSituation",
            "TypeSpecialSituation",
            "TypeTurno",        
            "TypeTermination",            
            "AmsRenovation",
            "ReqID",
            "UsrCreateID",
            "UsrUpdateID",
            "AmsMaximunDay",
            "AgtStatus"
          ]; // Lista de campos a excluir
          const dateFields = ["AmsBeginDate", "AmsEndDate","AmsTerminationDate","AmsEndDateMovement","AmsStartDateMovement","AmsAdmissionDate"]; // Lista de campos de fecha a formatear

          Object.keys(current).forEach(key => {
          
            if (!excludeFields.includes(key) && previous[key] !== current[key]) {
              let oldValue = previous[key]  || "Vacío";
              let newValue = current[key]   || "Vacío";

              // Si es salario, desencriptar antes de comparar
              if (key === "AmsSalary") {
                oldValue = this.canViewSalaryField ? this.$fun.decryptSalary(previous[key]) : previous[key];
                newValue = this.canViewSalaryField ? this.$fun.decryptSalary(current[key])  : current[key];

                if (oldValue === newValue) return; // No agregar si no hubo cambios reales
              }
              // Si es un campo de fecha, formatearlo
              if (dateFields.includes(key)) {
                oldValue = oldValue !== "Vacío" ? this.$fun.formatDateView(oldValue)  : oldValue;
                newValue = newValue !== "Vacío" ? this.$fun.formatDateView(newValue)  : newValue;
              }

              // Si es salario, desencriptarlo
              if (key === "AmsSalary") {
                oldValue = this.canViewSalaryField ? this.$fun.decryptSalary(previous[key]) : previous[key];
                newValue = this.canViewSalaryField ? this.$fun.decryptSalary(current[key])  : current[key];
              }

              changes[key] = { old: oldValue, new: newValue };
            }
          });
        }

        // Guardamos el movimiento con sus cambios (o vacío si no hay cambios)
        modified.push({ ...current, changes });
      }

      this.modifiedMovements = modified;
      //console.log("this.modifiedMovementssss",this.modifiedMovements);
    },

    prepareGroupByValues() {
      // Crear un mapa para almacenar el AgtID y su AmsTerminationDate si existe
      const terminationDates = {};

      // Primera pasada: Identificar si algún movimiento tiene AmsTerminationDate
      this.agreementMovemtsDetail.forEach(item => {
        if (item.AmsTerminationDate) {
          terminationDates[item.AgtID] = item.AmsTerminationDate; // Guardar la fecha de terminación
        }
      });

      // Segunda pasada: Asignar los valores correctos
      this.groupByValues = this.agreementMovemtsDetail.reduce((acc, item) => {
        if (!acc[item.AgtID]) {
          acc[item.AgtID] = {
            AgtBeginDate: item.AgtBeginDate,
            AgtEndDate: terminationDates[item.AgtID] ?? item.AgtEndDate, // Si hay AmsTerminationDate, usarla
          };
        }
        return acc;
      }, {});
      // this.groupByValues = this.agreementMovemtsDetail.reduce((acc, item) => {
      //   if (!acc[item.AgtID]) {
      //     acc[item.AgtID] = {
      //       AgtBeginDate: item.AgtBeginDate,
      //       AgtEndDate: item.AmsTerminationDate ?? item.AgtEndDate, // Usa AmsTerminationDate si está definida, sino AgtEndDate
      //     };
      //   }
      //   return acc;
      // }, {});
    },
    validateData() {
      //console.log("Data Laboral", this.item);

      if (!this.$fun.isAdmin() && !this.$fun.specialpermission) {
        if (this.assignedRequirementIds.length === 0) {
          this.$fun.alert("Usted no tiene requerimientos asignados. Registro de contratos bloqueado.", "warning");
          return false;
        }
        if (!this.item.ReqID) {
          this.$fun.alert("Debe seleccionar un requerimiento asignado para registrar el contrato.", "warning");
          return false;
        }
      }

      if (this.item.NtpID == null) {
        this.$fun.alert("Por favor seleccionar una Persona", "warning");
        return false;
      }

      if (this.item.TypePayroll == null) {
        this.$fun.focusFieldError(this, "payrollRef", "El campo Planilla es obligatorio");
        return false;
      }

      if (this.item.AreID == null) {
        this.$fun.focusFieldError(this, "areaRef", "El campo Area es obligatorio");
        return false;
      }

      if (this.item.CceID == null) {
        this.$fun.focusFieldError(this, "centerCostRef", "El campo Centro de Costo es obligatorio");
        return false;
      }

      if (this.item.BslID == null) {
        this.$fun.focusFieldError(this, "businessLineRef", "El campo Línea de Negocio es obligatorio");
        return false;
      }

      if (this.item.PstID == null) {
        this.$fun.focusFieldError(this, "positionRef", "El campo Cargo es obligatorio");
        return false;
      }

      if (this.item.EstID == null) {
        this.$fun.alert("El campo Establecimiento/Planta es obligatorio", "warning");
        return false;
      }

      if (this.item.TypeCategory == 4 && this.item.TypeFormatLabor == null) {
        this.$fun.alert("El campo Modalidad Formativa es obligatorio para personal en formación", "warning");
        return false;
      }

      if (!this.renovation && !this.extension && this.item.AgtAdmissionDate == null) {
        this.$fun.focusFieldError(this, "admissionDateRef", "Fecha de Ingreso es obligatoria");
        return false;
      }

      if (this.renovation && this.item.AgtBeginDate == null) {
        this.$fun.focusFieldError(this, "beginDateRef", "Inicio de Contrato es obligatorio");
        return false;
      }

      if (this.item.AgtBeginDate && this.item.AgtEndDate) {
        const begin = new Date(this.item.AgtBeginDate);
        const end = new Date(this.item.AgtEndDate);
        if (!isNaN(begin.getTime()) && !isNaN(end.getTime())) {
          if (end <= begin) {
            this.$fun.alert("La fecha de fin de contrato debe ser mayor a la fecha de inicio.", "warning");
            return false;
          }
        }
      }

      if (this.RegisterCessation) {
        if (this.item.TypeTermination == null) {
          this.$fun.focusFieldError(this, "terminationRef", "Motivo de Cese es obligatorio");
          return false;
        }
      }

      // Validación de contrato de temporada (máximo 10 meses seguidos)
      const selectComponent = this.$refs.typeContractRef;
      let rule = -1;
      if (selectComponent) {
        const genericComponent = selectComponent.$refs.sgeneric;
        if (genericComponent && genericComponent.items) {
          const obj = genericComponent.items.find(x => x.DedID === this.item.TypeContract || x.DedValue === this.item.TypeContract);
          rule = obj ? Number(obj.DedHelper2 ?? -1) : -1;
        }
      }

      if (rule === 0) {
        // En edición, si no tiene AgtAdmissionDate, tomamos la de ingreso original del contrato
        const admissionDate = this.item.AgtAdmissionDate || this.item.AmsAdmissionDate;
        if (admissionDate && this.item.AgtEndDate) {
          const admission = new Date(admissionDate);
          const end = new Date(this.item.AgtEndDate);
          if (!isNaN(admission.getTime()) && !isNaN(end.getTime())) {
            const diffTime = end - admission;
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
            if (diffDays > 304) {
              this.$fun.alert("El contrato de temporada no puede superar los 10 meses continuos (304 días) desde la fecha de ingreso.", "warning");
              return false;
            }
          }
        }
      }

      return true;
    },

    clear() {},

    close() {
      this.$refs.document.focus();
      this.$emit("toclose", 2);
      this.clear();
    },
    save() {

    },
    getProfession() {
      let obj = {
        prnStatus: 1,
      };
      _sProfService.list(obj, this.$fun.getUserID()).then((resp) => {
        if (resp.status == 200) {
          //console.log("tag", resp.data);
          this.profession = resp.data;
        }
      });
    },
    getEducationLevels() {
      sEducationLevel.list(this.$fun.getUserID()).then((resp) => {
        if (resp.status == 200) {
         // console.log("tag", resp.data);
          this.EducationLevels = resp.data;
          this.TypeEducationLevel = 1;
        }
      });
    },
    getQuadrille() {
      let obj = {
        SecStatus: 1,
      };
      _sQuadriService.list(obj, this.$fun.getUserID()).then((resp) => {
        if (resp.status == 200) {
         // console.log("tag", resp.data);
          this.quadrille = resp.data;
        }
      });
    },
    getPositiom() {
      let obj = { AreID: null, TypePosition: 1 };
      _sCttoService.Positionlist(obj, this.$fun.getUserID()).then((resp) => {
        if (resp.status == 200) {
         // console.log("tag", resp.data);
          this.Position = resp.data;
        }
      });
    },
    getData() {
      if (this.validateData()) {

        this.item.CceID               = this.selectedCenterCost;
        this.item.BslID               = this.selectedBusinessLine;
        this.item.ReqID               = this.item.ReqID || null;

        this.item.AgtTerminationDate  = this.RegisterCessation ? this.item.AgtTerminationDate   : null;
        this.item.TypeTermination     = this.RegisterCessation ? this.item.TypeTermination      : null;
        this.item.CmpID               = this.item.TypeSituation == 2 ? this.item.CmpID          : null;
        this.item.Supervision         = this.item.TypeSpecialSituation == 4 ? 0 : 1;

        this.item.AgtSalary = isNaN(this.item.AgtSalary)
          ? this.item.AgtSalary
          : this.$fun.encryptSalary(String(this.item.AgtSalary || 0));

        if(this.renovation){
          this.item.AgtTerminationDate  = null;
          this.item.TypeTermination     = null;
          this.item.AgtStatus = 1;
          this.item.SecStatus = 1;
        }

        if(this.item.AgtTerminationDate && this.item.TypeTermination){
          this.item.AgtStatus = 2;
        }


        this.item.LveID = null;
        this.item.EdiCode = null;
        this.item.EdmCode = null;
        this.item.PmaYearAdmission = null;
        this.item.PmaYearGraduation = null;

        return  this.item //{

          //AgtID : null , //this.AgtID,
         
          // AgtChildrenQuantity: this.item.AgtChildrenQuantity, // Num Hijos
          // AgtBeginDate: this.item.AgtBeginDate,
          // AgtEndDate: this.item.AgtEndDate,
          // AgtTerminationDate: this.item.RegisterCessation
          //   ? this.item.AgtTerminationDate
          //   : null,
          // AgtMaximunDay: this.item.AgtMaximunDay,
          // AgtSalary: this.item.AgtSalary,
          // AgtAdmissionDate: this.item.AgtAdmissionDate,
          // AgtStatus: this.item.AgtStatus,
          // //WkrID:null,
          // TypePayroll: this.item.TypePayroll,
          // AreID: this.item.AreID,
          // CceID: this.item.CceID,
          // TypeCategory: this.item.TypeCategory,
          // CmpID: this.item.CmpID,
          // GngID: this.item.GngID,
          // BslID: this.item.BslID,
          // TypeSituation: this.item.TypeSituation,
          // TypeSpecialSituation: this.item.TypeSpecialSituation,
          // TypeCivilStatus: this.item.TypeCivilStatus,
          // TypeWorker: this.item.TypeWorker,
          // TypeLaborRegime: this.item.TypeLaborRegime,
          // TypeOccupationalCategory: this.item.TypeOccupationalCategory,
          // LveID: this.item.LveID,
          // PstID: this.item.PstID,
          // PrnID: this.item.PrnID,
          // TypeContract: this.item.TypeContract,
          // TypeTermination: this.item.RegisterCessation
          //   ? this.item.TypeTermination
          //   : null,
          // TypeTurno: this.item.TypeTurno,
          // AgtRenovation: 0, // Renovacion
          // RqpID: 0, //requerimiento de Personal
          // //ceseActivo: this.item.ceseActivo,
          // //AmsID:null,//Movimiento de Contrato
          // SecStatus: 1,
          // UsrCreateID: this.$fun.getUserID(),
          // UsrUpdateID: this.$fun.getUserID(),

          // //Persona de Contrato
          // NtpID: this.item.NtpID,
        //};
      } else {
        return null;
      }
    },
    returnPerson(value, item) {
      if (value != null) {
        item.NtpFullName = value.NtpFullName;
        item.NtpID = value.NtpID;
      } else {
        //item.NtpFullName = null;
        item.NtpID = null;
      }
    },
    updateDates() {
        //console.log("Cambio de Fechas",this.contractDates);
        this.contractDates.AgtAdmissionDate = this.item.AgtAdmissionDate;
        this.contractDates.AgtEndDate       = this.item.AgtEndDate;
      },
    onCampaignSelected(cmpID) {
      if (!cmpID) {
        this.item.AgtEndDate = null;
        this.updateDates();
      }
    },
    onCampaignDatesLoaded(dates) {
      const campaignEndDate = dates?.CmpEndDate || null;
      this.item.AgtEndDate = campaignEndDate;
      this.updateDates();
    },
    getDateBeginAndEnd(){
      let obj = { AgtAdmissionDate: this.item.AgtAdmissionDate, AgtEndDate: this.item.AgtEndDate };
      return obj;
    },
    updateContractEndDate() {
      const dedID = this.item.TypeContract;
      if (dedID === null || dedID === undefined) {
        this.$set(this.item, '_endDateLocked', false);
        this.$set(this.item, '_isIndeterminate', false);
        return;
      }

      const selectComponent = this.$refs.typeContractRef;
      let items = [];
      if (selectComponent) {
        const genericComponent = selectComponent.$refs.sgeneric;
        if (genericComponent && genericComponent.items) {
          items = genericComponent.items;
        }
      }

      const obj = items.find(x => x.DedID === dedID || x.DedValue === dedID);
      const rule = obj ? Number(obj.DedHelper2 ?? -1) : -1;

      const refDate = this.item._reqEndDate || this.item._cmpEndDate || null;

      if (rule === 0) {
        if (refDate) {
          this.item.AgtEndDate = refDate;
        }
        this.$set(this.item, '_endDateLocked', true);
        this.$set(this.item, '_isIndeterminate', false);
      } else if (rule === 1) {
        if (refDate) {
          this.item.AgtEndDate = refDate;
        }
        this.$set(this.item, '_endDateLocked', false);
        this.$set(this.item, '_isIndeterminate', false);
      } else if (rule === 2) {
        this.item.AgtEndDate = null;
        this.$set(this.item, '_endDateLocked', true);
        this.$set(this.item, '_isIndeterminate', true);
      } else {
        this.$set(this.item, '_endDateLocked', false);
        this.$set(this.item, '_isIndeterminate', false);
      }
      this.updateDates();
    },
    onTypeContractChanged(dedID) {
      if (dedID === null || dedID === undefined) return;
      this.item.TypeContract = dedID;
      this.updateContractEndDate();
    },
  },
  watch: {
    "item.ReqID": {
      handler(newVal) {
        if (newVal) {
          this.fetchVacancyStatus(newVal);
        } else {
          this.vacancyStatus = { TotalVacancies: 0, CoveredVacancies: 0, ActiveContracts: 0 };
        }
      },
      immediate: true,
    },
    selectedSourceRequirementKey(newValue) {
      if (!newValue) {
        return;
      }

      this.handleSourceRequirementSelection(newValue);
    },
    selectedCenterCost(newValue) {
      this.item.CceID = newValue;
    },
    selectedBusinessLine(newValue) {
      this.item.BslID = newValue;
    },
    'item.PstID'(){   
       if (this.isLoadingRequirementData) return;
       if(!this.item.PstID) {   
         this.item.PstName = "";
         this.item.PstIsBascCritic = 0;
         this.item.PstIsBascCriticName = "";
         return;
       };

       _sPositionService.searchByPstID(this.item.PstID,this.$fun.getUserID()).then(x=>{ 
         let postion = x.data;
         console.log("cargo posicion = " , postion);   
         this.item.TypeLaborRegime = postion.TypeLaborRegime;
         this.item.LaborRegimeText = postion.LaborRegimeText;
         this.item.TypeWorker = postion.TypePosition;
         this.item.TypeWorkerName = postion.TypePositionName;
         this.item.PstIsBascCritic = postion.PstIsBascCritic;
         this.item.PstIsBascCriticName = postion.PstIsBascCriticName;
       });
     },
     'item.AreID'(newVal) {
       if (this.isLoadingRequirementData) return;
       console.log("Cambio de Área", newVal);
       if (!newVal) {
         this.item.AreName = "";
       }
     },
    'item.AgtAdmissionDate'(){      
        this.updateDates();
    },
    'item.AgtEndDate'(){     
      this.updateDates();
    }

  },
  created() {
    if (this.datawork) {
        this.initialize();
    } else {
        console.warn("datawork aún no está definido. Esperando su asignación.");
    }
    //this.initialize();
  },
  mounted(){
    this.$refs.PersonRef.toggleSaveData(this.item.PrsDocumentNumber)
    //this.item.RegisterCessation = false; // Booleano
    this.updateDates();
  }
};
</script>
<style scoped>
.transparent-table {
  background: transparent !important;
}
.transparent-table th, .transparent-table td {
  border-bottom: 1px solid rgba(0, 0, 0, 0.08) !important;
}
.border-t-dotted {
  border-top: 1px dashed rgba(0, 0, 0, 0.15);
}
</style>
